import { graphql } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { CommentsType } from './__types__/useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery.ts';
import type {
	SummaryType,
	useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery as useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery$data,
	useCombinedCommentsSummariesQueriesFooterCommentsSummaryQueryVariables as useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery$variables,
} from './__types__/useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery.js';
import type {
	useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery as useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery$data,
	useCombinedCommentsSummariesQueriesInlineCommentsSummaryQueryVariables as useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery$variables,
} from './__types__/useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery.ts';
export type CombinedCommentsSummariesData = {
	footerCommentsSummaryData:
		| useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery$data
		| undefined;
	inlineCommentsSummaryData:
		| useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery$data
		| undefined;
};

export type CombinedCommentsSummariesErrors = {
	footerCommentsSummaryError: ApolloErrorInterop | undefined;
	inlineCommentsSummaryError: ApolloErrorInterop | undefined;
};

type CombinedCommentsQueriesSummariesProps = {
	contentId: string;
	contentType: SummaryType;
	shouldSkipFooterCommentsQuery?: boolean;
	shouldSkipInlineCommentsQuery?: boolean;
};

type CombinedCommentsSummariesQueries = {
	data: CombinedCommentsSummariesData;
	errors: CombinedCommentsSummariesErrors;
	loading: boolean;
	refetch: () => void;
};

export const useCombinedCommentsSummariesQueries = ({
	contentId,
	contentType,
	shouldSkipFooterCommentsQuery = false,
	shouldSkipInlineCommentsQuery = false,
}: CombinedCommentsQueriesSummariesProps): CombinedCommentsSummariesQueries => {
	const {
		data: footerCommentsSummaryData,
		loading: footerCommentsQueryLoading,
		error: footerCommentsSummaryError,
		refetch: footerCommentsRefetch,
	} = useQueryInterop<
		useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery$data,
		useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery$variables
	>(
		graphql`
			query useCombinedCommentsSummariesQueriesFooterCommentsSummaryQuery(
				$contentId: ID!
				$contentType: SummaryType!
				$commentsType: CommentsType!
			) {
				getCommentsSummary(
					contentId: $contentId
					contentType: $contentType
					commentsType: $commentsType
				) {
					contentId
					summary
					summaryId
					language
				}
			}
		`,
		{
			variables: {
				contentId,
				contentType,
				commentsType: CommentsType.FOOTER,
			},
			notifyOnNetworkStatusChange: true,
			skip: shouldSkipFooterCommentsQuery,
		},
	);

	const {
		data: inlineCommentsSummaryData,
		loading: inlineCommentsQueryLoading,
		error: inlineCommentsSummaryError,
		refetch: inlineCommentsRefetch,
	} = useQueryInterop<
		useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery$data,
		useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery$variables
	>(
		graphql`
			query useCombinedCommentsSummariesQueriesInlineCommentsSummaryQuery(
				$contentId: ID!
				$contentType: SummaryType!
				$commentsType: CommentsType!
			) {
				getCommentsSummary(
					contentId: $contentId
					contentType: $contentType
					commentsType: $commentsType
				) {
					contentId
					summary
					summaryId
					language
				}
			}
		`,
		{
			variables: {
				contentId,
				contentType,
				commentsType: CommentsType.INLINE,
			},
			notifyOnNetworkStatusChange: true,
			skip: shouldSkipInlineCommentsQuery,
		},
	);

	const refetch = () => {
		void footerCommentsRefetch();
		void inlineCommentsRefetch();
	};

	return {
		data: {
			footerCommentsSummaryData,
			inlineCommentsSummaryData,
		},
		loading: footerCommentsQueryLoading || inlineCommentsQueryLoading,
		errors: {
			footerCommentsSummaryError,
			inlineCommentsSummaryError,
		},
		refetch,
	};
};
